'use client';

import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { BillingAddress } from '@/services/CustomerApiService';
import { useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { extractErrorMessage } from '@/lib/customer-panel/error-handler';
import { ErrorMessage, SuccessMessage, LoadingSpinner } from '@/lib/customer-panel/ui-components';
import CountryCitySelect, { type Country } from '@/components/ui/CountryCitySelect';

interface BillingAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: BillingAddress) => Promise<void>;
  initialData?: BillingAddress | null;
  translations?: Record<string, string>;
  countries: Country[];
}

export default function BillingAddressModal({
  isOpen,
  onClose,
  onSave,
  initialData = null,
  translations = {},
  countries,
}: BillingAddressModalProps) {
  const t = useCustomerPanelTranslations(translations);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<BillingAddress>(() => ({
    type: initialData?.type || 'individual',
    title: initialData?.title || '',
    is_default: initialData?.is_default || false,
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    tc_no: initialData?.tc_no || '',
    company_name: initialData?.company_name || '',
    tax_number: initialData?.tax_number || '',
    tax_office: initialData?.tax_office || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    country: initialData?.country || '',
    postal_code: initialData?.postal_code || '',
    phone: initialData?.phone || '',
    email: initialData?.email || '',
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await onSave(formData);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      setError(extractErrorMessage(err, t('customer_panel.common.error', 'An error occurred')));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback((field: keyof BillingAddress, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const inputBaseClasses = "border border-gray-200 focus:border-alsero-first focus:ring-1 focus:ring-alsero-first/30 transition-all duration-200 rounded-lg text-[#4F545A] font-medium text-xs px-2.5 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed w-full";

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] overflow-y-auto">
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        <div className="flex min-h-full items-center justify-center p-4">
          <motion.div
            className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="sticky top-0 bg-gray-50/95 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between z-10 rounded-t-xl">
              <h2 className="text-sm font-semibold text-gray-900">
                {initialData?.id 
                  ? t('customer_panel.billing.edit_address', 'Edit Billing Address')
                  : t('customer_panel.billing.add_address', 'Add Billing Address')
                }
              </h2>
              <button type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={t('customer_panel.common.close', 'Close')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-2.5" noValidate>
              {error && <ErrorMessage message={error} />}
              {success && <SuccessMessage message={t('customer_panel.common.update_success', 'Successfully updated')} />}

              <div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-3 border border-gray-100">
                <label className="font-medium text-xs text-gray-700">
                  {t('customer_panel.billing.type', 'Invoice Type')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex gap-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value="individual"
                      checked={formData.type === 'individual'}
                      onChange={(e) => handleChange('type', e.target.value as 'individual' | 'corporate')}
                      className="w-3.5 h-3.5 text-alsero-first focus:ring-alsero-first"
                    />
                    <span className="text-xs text-gray-700">{t('customer_panel.billing.individual', 'Individual')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value="corporate"
                      checked={formData.type === 'corporate'}
                      onChange={(e) => handleChange('type', e.target.value as 'individual' | 'corporate')}
                      className="w-3.5 h-3.5 text-alsero-first focus:ring-alsero-first"
                    />
                    <span className="text-xs text-gray-700">{t('customer_panel.billing.corporate', 'Corporate')}</span>
                  </label>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="title" className="font-medium text-xs text-gray-600">
                  {t('customer_panel.billing.title', 'Address Title')}
                  <span className="text-gray-400 text-[10px] ml-1">({t('customer_panel.billing.title_hint', 'e.g., Home, Office')})</span>
                </label>
                <motion.input
                  type="text"
                  id="title"
                  value={formData.title || ''}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className={inputBaseClasses}
                  placeholder={t('customer_panel.billing.title_placeholder', 'e.g., Home, Office')}
                  whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                  whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                  transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                />
              </div>

              {formData.type === 'individual' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex flex-col gap-1">
                    <label htmlFor="first_name" className="font-medium text-xs text-gray-600">
                      {t('customer_panel.profile.first_name', 'First Name')}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <motion.input
                      type="text"
                      id="first_name"
                      value={formData.first_name || ''}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      required
                      className={inputBaseClasses}
                      whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                      whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                      transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label htmlFor="last_name" className="font-medium text-xs text-gray-600">
                      {t('customer_panel.profile.last_name', 'Last Name')}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <motion.input
                      type="text"
                      id="last_name"
                      value={formData.last_name || ''}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      required
                      className={inputBaseClasses}
                      whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                      whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                      transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                    />
                  </div>

                  {formData.country === 'TR' && (
                    <div className="flex flex-col gap-1 md:col-span-2">
                      <label htmlFor="tc_no" className="font-medium text-xs text-gray-600">
                        {t('customer_panel.billing.tc_no', 'TC Identity Number')}
                        <span className="text-gray-400 text-[10px] ml-1">({t('customer_panel.billing.optional', 'optional')})</span>
                      </label>
                      <motion.input
                        type="text"
                        id="tc_no"
                        value={formData.tc_no || ''}
                        onChange={(e) => handleChange('tc_no', e.target.value.replace(/\D/g, '').slice(0, 11))}
                        className={inputBaseClasses}
                        maxLength={11}
                        placeholder="11 digits"
                        whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                        whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                        transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                      />
                    </div>
                  )}
                </div>
              )}

              {formData.type === 'corporate' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex flex-col gap-1">
                    <label htmlFor="company_name" className="font-medium text-xs text-gray-600">
                      {t('customer_panel.billing.company_name', 'Company Name')}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <motion.input
                      type="text"
                      id="company_name"
                      value={formData.company_name || ''}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      required
                      className={inputBaseClasses}
                      whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                      whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                      transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="tax_number" className="font-medium text-xs text-gray-600">
                      {t('customer_panel.billing.tax_number', 'Tax Number (VKN)')}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <motion.input
                      type="text"
                      id="tax_number"
                      value={formData.tax_number || ''}
                      onChange={(e) => handleChange('tax_number', e.target.value)}
                      required
                      className={inputBaseClasses}
                      whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                      whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                      transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                    />
                  </div>

                  <div className="flex flex-col gap-1 md:col-span-2">
                    <label htmlFor="tax_office" className="font-medium text-xs text-gray-600">
                      {t('customer_panel.billing.tax_office', 'Tax Office')}
                    </label>
                    <motion.input
                      type="text"
                      id="tax_office"
                      value={formData.tax_office || ''}
                      onChange={(e) => handleChange('tax_office', e.target.value)}
                      className={inputBaseClasses}
                      whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                      whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                      transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label htmlFor="address" className="font-medium text-xs text-gray-600">
                    {t('customer_panel.profile.address', 'Address')}
                  </label>
                  <motion.textarea
                    id="address"
                    value={formData.address || ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    rows={2}
                    className={inputBaseClasses + " resize-y min-h-[52px]"}
                    whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                    whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                    transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                  />
                </div>

                <div className="md:col-span-2">
                  <CountryCitySelect
                    countries={countries}
                    countryValue={formData.country || null}
                    cityValue={formData.city || null}
                    onCountryChange={(countryId, countryCode) => {
                      handleChange('country', countryCode || '');
                    }}
                    onCityChange={(cityId, cityName) => {
                      handleChange('city', cityName || '');
                    }}
                    countryPlaceholder={t('customer_panel.profile.select_country', 'Select Country')}
                    cityPlaceholder={t('customer_panel.profile.select_city', 'Select City')}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="postal_code" className="font-medium text-xs text-gray-600">
                    {t('customer_panel.profile.postal_code', 'Postal Code')}
                  </label>
                  <motion.input
                    type="text"
                    id="postal_code"
                    value={formData.postal_code || ''}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    className={inputBaseClasses}
                    maxLength={20}
                    whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                    whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                    transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="phone" className="font-medium text-xs text-gray-600">
                    {t('customer_panel.profile.phone', 'Phone')}
                  </label>
                  <motion.input
                    type="tel"
                    id="phone"
                    value={formData.phone || ''}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className={inputBaseClasses}
                    whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                    whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                    transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="email" className="font-medium text-xs text-gray-600">
                    {t('customer_panel.profile.email', 'Email')}
                  </label>
                  <motion.input
                    type="email"
                    id="email"
                    value={formData.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={inputBaseClasses}
                    whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                    whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                    transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default || false}
                  onChange={(e) => handleChange('is_default', e.target.checked)}
                  className="w-3.5 h-3.5 text-alsero-first focus:ring-alsero-first border-gray-300 rounded"
                />
                <label htmlFor="is_default" className="text-xs text-gray-600 cursor-pointer">
                  {t('customer_panel.billing.set_as_default', 'Set as default billing address')}
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-3 mt-1 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                >
                  {t('customer_panel.common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-1.5 text-xs font-semibold bg-alsero-first text-white rounded-lg hover:bg-alsero-second focus:ring-2 focus:ring-alsero-first/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" className="inline-block mr-2" />
                      {t('customer_panel.common.loading', 'Loading...')}
                    </>
                  ) : (
                    t('customer_panel.common.save', 'Save')
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );

  if (typeof window !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
}
